import React, { Fragment } from "react";
import { twMerge } from "tailwind-merge";

import { cellBaseClassName } from "src/dataTable/ResultCell";
import {
  isLargeStringValue,
  renderLargeString,
} from "src/dataTable/TableUtils";
import { CellContent, isReactNode } from "src/dataTable/types";
import { Tooltip } from "src/design-system/Tooltip";
import { speakPythonPrimitive } from "src/utils/speakPython";

type Props<T> = {
  cell: CellContent<T> | React.ReactElement;
  className?: string;
  classNameOverrides?: string;
  withTooltip?: boolean;
  suffix?: React.ReactNode;
};

export const RawCell = <T,>({
  cell,
  className = cellBaseClassName,
  classNameOverrides,
  suffix,
  withTooltip = false,
}: Props<T>) => {
  const value = (() => {
    if (isReactNode(cell)) {
      return cell;
    }

    const val = cell.getValue();

    return typeof val === "boolean"
      ? speakPythonPrimitive(val.toString())
      : speakPythonPrimitive(val);
  })() as React.ReactNode;

  const Wrapper = withTooltip
    ? ({ children }: { children: React.ReactNode }) => (
        <Tooltip
          disabled={!(value !== undefined && value !== "")}
          placement="top"
          title={String(value)}
          asChild
        >
          {children}
        </Tooltip>
      )
    : Fragment;

  return (
    <Wrapper>
      <div
        className={twMerge(
          "flex flex-row items-center justify-between gap-x-0.5 pl-2",
          className,
          classNameOverrides,
        )}
      >
        {Boolean(suffix) || typeof value === "string" ? (
          <>
            <div className="flex-1 truncate">
              {isLargeStringValue(value) ? renderLargeString(value) : value}
            </div>
            {suffix}
          </>
        ) : (
          value
        )}
      </div>
    </Wrapper>
  );
};
